import { useState, useRef, useEffect } from 'react';
import { Plus, CheckCheck, X, Search } from 'lucide-react';
import POIItem from './POIItem';
import GeofenceItem from './GeofenceItem';
import { useERSContext } from '@/context';
import { useMapContext } from '@/context';
import { StatusTabs } from '../StatusTabs/StatusTabs';
import { Button } from '@/components';
import { showGeofence, removeGeofence, showMarker, removeMarker } from '@/pages/mapview/MapCommon';

export default function MapTreeList() {
  const [activeTab, setActiveTab] = useState('geofences');
  const [searchFilter, setSearchFilter] = useState('');

  const [checkedGeofenceIds, setCheckedGeofenceIds] = useState([]);
  const [editableGeofenceIds, setEditableGeofenceIds] = useState([]);
  const [checkedPoiIds, setCheckedPoiIds] = useState([]);
  const [editablePoiIds, setEditablePoiIds] = useState([]);
  const { mapRef, mapMarkersRef, mapLinesRef, mapDrawRef } = useMapContext();

  const { POIs, setPOIs, selfProfileRef, geofences, setGeofences, checkedPOIsRef, checkedGeofencesRef } =
    useERSContext();

  const treeNodeItemRefs = useRef({});

  // Keep the ERS context ref in sync so the popup map window
  // can re-send checked POI markers when it opens
  useEffect(() => {
    checkedPOIsRef.current = POIs.filter(p => checkedPoiIds.includes(p.id));
  }, [checkedPoiIds, POIs]);
  useEffect(() => {
    checkedGeofencesRef.current = geofences.filter(g => checkedGeofenceIds.includes(g.id));
  }, [checkedGeofenceIds, geofences]);

  const tabs = [
    { key: 'geofences', label: 'Geofences' },
    { key: 'pois', label: 'POIs' },
  ];

  function createPOI() {
    const id = 'poi-' + Date.now();
    const center = mapRef.current?.getCenter() || [0, 0];
    const newPOI = {
      id,
      displayName: id,
      description: '',
      severity: 1,
      color: '#000000',
      type: 'poi',
      creationTimestamp: Math.floor(Date.now() / 1000),
      editPermission: 'dispatchers',
      organizationId: selfProfileRef.current.organizationId,
      exposure: { type: 'all', contacts: [] },
      location: [center.lng, center.lat],
      perimeter: 0,
      isNew: true,
    };
    setPOIs(prev => [newPOI, ...prev]);
  }

  function createGeoFence() {
    const name = 'new-' + Date.now();
    const newGeofence = {
      id: name,
      displayName: name,
      description: '',
      severity: 1,
      proximity: 0,
      active: true,
      color: '#000000',
      effected_users: [],
      coordinates: [],
      dynamicGroups: false,
      alert_on_enter: false,
      alert_on_leave: false,
      alarm: false,
      receivers: [],
      editable: true,
      isNew: true,
    };
    setGeofences(prev => [newGeofence, ...prev]);
  }

  const sortedGeofences = [...geofences]
    .filter(g => !searchFilter || g.displayName?.toLowerCase().includes(searchFilter.toLowerCase()))
    .sort((a, b) => {
      if (!a.creationTimestamp && b.creationTimestamp) return -1;
      if (a.creationTimestamp && !b.creationTimestamp) return 1;
      return (b.creationTimestamp || 0) - (a.creationTimestamp || 0);
    });

  const sortedPOIs = [...POIs]
    .filter(p => !searchFilter || p.displayName?.toLowerCase().includes(searchFilter.toLowerCase()))
    .sort((a, b) => {
      if (!a.creationTimestamp && b.creationTimestamp) return -1;
      if (a.creationTimestamp && !b.creationTimestamp) return 1;
      return (b.creationTimestamp || 0) - (a.creationTimestamp || 0);
    });

  const handleToggleAllGeofences = () => {
    const allGeofenceIds = sortedGeofences.map(g => g.id);
    const allChecked = allGeofenceIds.every(id => checkedGeofenceIds.includes(id));

    if (allChecked) {
      // Deselect all
      sortedGeofences.forEach(g => {
        removeGeofence({ id: g.id, map: mapRef.current, mapLinesObj: mapLinesRef.current });
      });
      setCheckedGeofenceIds([]);
    } else {
      // Select all
      sortedGeofences.forEach(g => {
        if (!checkedGeofenceIds.includes(g.id) && g.coordinates && g.coordinates.length > 0) {
          showGeofence({
            item: g,
            coordinates: g.coordinates,
            map: mapRef.current,
            mapLinesObj: mapLinesRef.current,
          });
        }
      });
      setCheckedGeofenceIds(allGeofenceIds);
    }
  };

  const handleToggleAllPOIs = () => {
    const allPoiIds = sortedPOIs.map(p => p.id);
    const allChecked = allPoiIds.every(id => checkedPoiIds.includes(id));

    if (allChecked) {
      // Deselect all
      sortedPOIs.forEach(p => {
        removeMarker({ category: 'poi', id: p.id, markersObj: mapMarkersRef.current });
      });
      setCheckedPoiIds([]);
    } else {
      // Select all
      sortedPOIs.forEach(p => {
        if (!checkedPoiIds.includes(p.id) && p.location && p.location.length === 2) {
          showMarker({
            category: 'poi',
            item: p,
            map: mapRef.current,
            markersObj: mapMarkersRef.current,
          });
        }
      });
      setCheckedPoiIds(allPoiIds);
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg">
      {/* Sub-tabs */}
      <div className="flex flex-row justify-between p-3 gap-2">
        <StatusTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          dataCount={{
            geofences: geofences.length,
            pois: POIs.length,
          }}
        />
        <Button
          className="p-3"
          variant="default"
          size="icon"
          onClick={activeTab === 'geofences' ? createGeoFence : createPOI}
          aria-label={activeTab === 'geofences' ? 'Add Geofence' : 'Add POI'}
        >
          <Plus size={'1rem'} />
        </Button>
      </div>

      {/* Search */}
      <div className="px-3 pb-3">
        <div className="border-2 border-border rounded-md flex items-center focus-within:border-primary transition-colors">
          <button
            onClick={activeTab === 'geofences' ? handleToggleAllGeofences : handleToggleAllPOIs}
            className="shrink-0 h-full px-3 flex items-center justify-center hover:bg-subtle transition-colors border-r border-border"
            title={
              activeTab === 'geofences'
                ? sortedGeofences.every(g => checkedGeofenceIds.includes(g.id)) && sortedGeofences.length > 0
                  ? 'Deselect All'
                  : 'Select All'
                : sortedPOIs.every(p => checkedPoiIds.includes(p.id)) && sortedPOIs.length > 0
                  ? 'Deselect All'
                  : 'Select All'
            }
          >
            {activeTab === 'geofences' ? (
              sortedGeofences.every(g => checkedGeofenceIds.includes(g.id)) && sortedGeofences.length > 0 ? (
                <X size={16} />
              ) : (
                <CheckCheck size={16} />
              )
            ) : sortedPOIs.every(p => checkedPoiIds.includes(p.id)) && sortedPOIs.length > 0 ? (
              <X size={16} />
            ) : (
              <CheckCheck size={16} />
            )}
          </button>
          <div className="flex flex-1 items-center gap-2 px-3.5 py-2">
            <Search size={15} className="text-icon-muted" />
            <input
              type="text"
              placeholder="Search..."
              className="outline-none text-sm w-full bg-transparent text-text"
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {activeTab === 'geofences' && (
          <>
            {sortedGeofences.length === 0 ? (
              <div className="text-center text-muted py-8">No geofences found</div>
            ) : (
              <ul className="space-y-2">
                {sortedGeofences.map((g, i) => (
                  <li key={g.id || i} ref={el => (treeNodeItemRefs.current['geofence' + g.id] = el)}>
                    <GeofenceItem
                      g={g}
                      checkedGeofenceIds={checkedGeofenceIds}
                      setCheckedGeofenceIds={setCheckedGeofenceIds}
                      editableGeofenceIds={editableGeofenceIds}
                      setEditableGeofenceIds={setEditableGeofenceIds}
                      map={mapRef.current}
                      mapDraw={mapDrawRef.current}
                      mapLinesObj={mapLinesRef.current}
                      geofences={geofences}
                      setGeofences={setGeofences}
                      alertReceivers={[selfProfileRef.current?.userId]}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {activeTab === 'pois' && (
          <>
            {sortedPOIs.length === 0 ? (
              <div className="text-center text-muted py-8">No POIs found</div>
            ) : (
              <ul className="space-y-2">
                {sortedPOIs.map((poi, i) => (
                  <li key={poi.id || i} ref={el => (treeNodeItemRefs.current['poi' + poi.id] = el)}>
                    <POIItem
                      poi={poi}
                      checkedPoiIds={checkedPoiIds}
                      setCheckedPoiIds={setCheckedPoiIds}
                      editablePoiIds={editablePoiIds}
                      setEditablePoiIds={setEditablePoiIds}
                      map={mapRef.current}
                      markersObj={mapMarkersRef.current}
                      POIs={POIs}
                      setPOIs={setPOIs}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
